/**
 * WindStreakSpawner.ts — emits horizontal wind-streak particles for visual
 * wind direction / speed feedback.
 *
 * Particles (kind=PARTICLE_KIND_WIND) are spawned at random positions in a
 * square centred on the player boat, travel with the wind velocity, and are
 * rendered as thin velocity-aligned dashes by the particle shader.
 *
 * No gravity or drag is applied to them (see ParticleSystem.step).
 */

import { ParticleSystem, PARTICLE_KIND_WIND } from "./ParticleSystem";

/** Half-side of the spawn square around the focus point (metres). */
const AREA_HALF = 130;

/** Min / max height above water for spawning (metres). */
const Y_MIN = 0.3;
const Y_MAX = 2.8;

/** Particle lifetime range (seconds). */
const LIFE_MIN = 3.5;
const LIFE_MAX = 5.5;

/** Spawn rate at reference wind speed (particles / second). */
const RATE_AT_REF = 65;
const REFERENCE_SPEED = 6; // m/s

/** Base particle scale used by the shader to size the streak (metres). */
const STREAK_SIZE = 1.5;

/**
 * Step this every frame, passing the current wind parameters and the world
 * position to centre the spawn volume on (e.g. player boat).
 */
export class WindStreakSpawner {
  private acc = 0;

  constructor(private readonly system: ParticleSystem) {}

  step(
    dt: number,
    windDirRad: number,
    windSpeed: number,
    waterLevel: number,
    centerX: number,
    centerZ: number,
  ): void {
    if (windSpeed < 0.5) return;

    // Wind velocity components (meteorological → Cartesian).
    const vx = -Math.sin(windDirRad) * windSpeed;
    const vz = Math.cos(windDirRad) * windSpeed;

    // Spawn rate scales with wind speed, capped at 2× the reference.
    const rate = RATE_AT_REF * Math.min(windSpeed / REFERENCE_SPEED, 2.0);
    this.acc += rate * dt;

    while (this.acc >= 1) {
      this.acc -= 1;

      // Scatter uniformly inside the square.
      const x = centerX + (Math.random() * 2 - 1) * AREA_HALF;
      const z = centerZ + (Math.random() * 2 - 1) * AREA_HALF;
      const y = waterLevel + Y_MIN + Math.random() * (Y_MAX - Y_MIN);

      const life = LIFE_MIN + Math.random() * (LIFE_MAX - LIFE_MIN);

      // Small random jitter so streaks aren't perfectly uniform.
      const jitter = 0.12;
      const jx = vx * (1 + (Math.random() - 0.5) * jitter);
      const jz = vz * (1 + (Math.random() - 0.5) * jitter);

      this.system.spawn(
        x,
        y,
        z,
        jx,
        0, // no vertical velocity
        jz,
        life,
        STREAK_SIZE,
        STREAK_SIZE,
        PARTICLE_KIND_WIND,
      );
    }
  }
}
